import { Component, EventEmitter, Input, Output } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { IxellAPIService, StatsGraphDataX, StatsGraphDataY, StatsGraphFilterInfo, StatsGraphFilterKind, StatsGraphPrefs, StatsGraphProps } from 'src/app/tools/api.service';
import { StorageService } from '../../tools/storage.service';

export enum FilterType {
  TEXTBOX,
  TEXTBOXADVANCED,
  NUMUPDOWN,
  DROPDOWN,
  CHECKBOX
}

export interface FilterProps {
  kind: StatsGraphFilterKind;
  type: FilterType;
  isRight: boolean;
  name: string;
  isRequired: boolean;
  min: number | null;
  max: number | null;
}

const filtersProps: FilterProps[] = [
  { kind: StatsGraphFilterKind.SPLITCOUNTRY,    type: FilterType.CHECKBOX,        isRight: true,  isRequired: false, min: null, max: null, name: "stats.graphs.filters.splitByCountry" },
  { kind: StatsGraphFilterKind.SPLITUSERTYPE,   type: FilterType.CHECKBOX,        isRight: true,  isRequired: false, min: null, max: null, name: "stats.graphs.filters.splitByUserType" },
  { kind: StatsGraphFilterKind.SPLITBALANCES,   type: FilterType.CHECKBOX,        isRight: true,  isRequired: false, min: null, max: null, name: "stats.graphs.filters.splitBalances" },

  { kind: StatsGraphFilterKind.CWTSOURCEENGINE, type: FilterType.DROPDOWN,        isRight: true,  isRequired: false, min: null, max: null, name: "stats.graphs.filters.searchEngine.name" },
  { kind: StatsGraphFilterKind.CWTCOLORCODE,    type: FilterType.TEXTBOXADVANCED, isRight: false, isRequired: false, min: null, max: null, name: "search.colorCode" },
  { kind: StatsGraphFilterKind.CWTDESIG,        type: FilterType.TEXTBOXADVANCED, isRight: false, isRequired: false, min: null, max: null, name: "search.designation" },
  { kind: StatsGraphFilterKind.CWTFORMULA,      type: FilterType.NUMUPDOWN,       isRight: false, isRequired: false, min: null, max: null, name: "search.formuleNum" },
  { kind: StatsGraphFilterKind.CWTTEINTIER,     type: FilterType.TEXTBOX,         isRight: false, isRequired: false, min: null, max: null, name: "search.shadeGuideNum" },
  { kind: StatsGraphFilterKind.CWTGROUP,        type: FilterType.DROPDOWN,        isRight: false, isRequired: false, min: null, max: null, name: "search.color" },
  { kind: StatsGraphFilterKind.CWTMANUFACTURER, type: FilterType.TEXTBOX,         isRight: false, isRequired: false, min: null, max: null, name: "common.manufacturer" },
  { kind: StatsGraphFilterKind.CWTMODEL,        type: FilterType.TEXTBOX,         isRight: false, isRequired: false, min: null, max: null, name: "search.model" },
  { kind: StatsGraphFilterKind.CWTYEAR,         type: FilterType.NUMUPDOWN,       isRight: false, isRequired: false, min: null, max: null, name: "search.year" },
  { kind: StatsGraphFilterKind.CWTAPP,          type: FilterType.DROPDOWN,        isRight: false, isRequired: false, min: null, max: null, name: "search.application" },
  { kind: StatsGraphFilterKind.CWTTECHNO,       type: FilterType.DROPDOWN,        isRight: false, isRequired: false, min: null, max: null, name: "common.technology" },

  { kind: StatsGraphFilterKind.CBMANUFACTURER,  type: FilterType.DROPDOWN,        isRight: false, isRequired: false, min: null, max: null, name: "common.manufacturer" },
  { kind: StatsGraphFilterKind.CBGAMME,         type: FilterType.DROPDOWN,        isRight: false, isRequired: false, min: null, max: null, name: "stats.graphs.filters.gamme" },
  { kind: StatsGraphFilterKind.CBSERIE,         type: FilterType.DROPDOWN,        isRight: false, isRequired: false, min: null, max: null, name: "stats.graphs.filters.serie" },
  { kind: StatsGraphFilterKind.CBMODEL,         type: FilterType.DROPDOWN,        isRight: false, isRequired: false, min: null, max: null, name: "stats.graphs.filters.model" },
  { kind: StatsGraphFilterKind.CBPART,          type: FilterType.DROPDOWN,        isRight: false, isRequired: false, min: null, max: null, name: "stats.graphs.filters.part" },
  { kind: StatsGraphFilterKind.CBSHOWTYPE,      type: FilterType.DROPDOWN,        isRight: true,  isRequired: true,  min: null, max: null, name: "stats.graphs.filters.showType.name" },

  { kind: StatsGraphFilterKind.SPLITVARIANTS,   type: FilterType.CHECKBOX,        isRight: true,  isRequired: false, min: null, max: null, name: "stats.graphs.filters.splitVariants" },
  { kind: StatsGraphFilterKind.SEETOP,          type: FilterType.NUMUPDOWN,       isRight: true,  isRequired: true,  min: 1,    max: 50,   name: "stats.graphs.filters.seeTop" },

  { kind: StatsGraphFilterKind.FEATURETYPE,     type: FilterType.DROPDOWN,        isRight: false, isRequired: false, min: null, max: null, name: "stats.graphs.filters.featureType.name" },

  { kind: StatsGraphFilterKind.TIMECUT,         type: FilterType.DROPDOWN,        isRight: false, isRequired: false, min: null, max: null, name: "stats.graphs.filters.timecut.name" },

  { kind: StatsGraphFilterKind.USERID,          type: FilterType.TEXTBOX,         isRight: false, isRequired: false, min: null, max: null, name: "stats.graphs.filters.userid" },

  { kind: StatsGraphFilterKind.ORDERFILTERBY,   type: FilterType.DROPDOWN,        isRight: false, isRequired: false, min: null, max: null, name: "stats.graphs.filters.orderfilterby.name" },

  { kind: StatsGraphFilterKind.LOGTYPE,         type: FilterType.DROPDOWN,        isRight: false, isRequired: true, min: null, max: null, name: "search.type" },
  { kind: StatsGraphFilterKind.LOGCAT,          type: FilterType.TEXTBOXADVANCED, isRight: false, isRequired: false, min: null, max: null, name: "stats.graphs.filters.errorCode" }
];

@Component({
  selector: 'stats-filters',
  templateUrl: './filters.component.html',
  styleUrls: ['./filters.component.scss']
})

export class StatsFiltersComponent {
  @Input() graphProps: StatsGraphProps;
  @Input() graphQuery: EventEmitter<void>;
  @Output() header = new EventEmitter<string>();
  @Output() submit = new EventEmitter<any>();
  @Output() sendValues = new EventEmitter<StatsGraphPrefs>();
  @Output() editValues = new EventEmitter<void>();

  StatsGraphDataX = StatsGraphDataX;
  StatsGraphDataY = StatsGraphDataY;
  StatsGraphFilterKind = StatsGraphFilterKind;
  FilterType = FilterType;

  filtersForm = new UntypedFormGroup({});

  statsVisitsDateMin: Date;
  datenow: Date = new Date;

  canDateSub: boolean = false;

  private onLangChangeSub: any;
  private statsGetEarliestDateForDataSub: any;
  private graphQuerySub: any;
  private valueChangesSDSub: any;
  private valueChangesEDSub: any;
  private valueChangesPRSub: any;


  constructor(private apiService: IxellAPIService, private translate: TranslateService, private storage: StorageService) { }
  

  startDateValidator(fg: UntypedFormGroup, reverse: boolean): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      let always = fg.controls["period"].value == '0';
      if (always)
        return null;

      let start = control.value;
      let ndd = fg.controls[reverse ? "startDate" : "endDate"];
      if (ndd == undefined)
        return null;

      let end = ndd.value;

      return (reverse ? (end > start) : (start > end)) ? { wrongDate: { value: control.value } } : null;
    };
  }

  ngOnInit() {
    if (this.graphProps.graph.dataX == StatsGraphDataX.DATE) {
      let hasStart = (this.graphProps.graph.latestDateStart != null);
      let hasEnd = (this.graphProps.graph.latestDateEnd != null);

      this.filtersForm.addControl("period", new UntypedFormControl((hasStart || hasEnd) ? 1 : 0));
      this.filtersForm.addControl("startDate", new UntypedFormControl(new Date(), this.startDateValidator(this.filtersForm, false)));
      this.filtersForm.addControl("endDate", new UntypedFormControl(new Date(), this.startDateValidator(this.filtersForm, true)));

      this.statsGetEarliestDateForDataSub = this.apiService.statsGetEarliestDateForData(this.graphProps.graph.dataY).subscribe(data => {
        this.statsVisitsDateMin = data;

        if (hasStart)
          this.filtersForm.controls["startDate"].setValue(this.graphProps.graph.latestDateStart);
        else
          this.filtersForm.controls["startDate"].setValue(data);

        this.canDateSub = true;

        if (this.statsGetEarliestDateForDataSub != null) this.statsGetEarliestDateForDataSub.unsubscribe();
      });

      if (hasEnd) {
        this.filtersForm.controls["endDate"].setValue(this.graphProps.graph.latestDateEnd);
      }
    }

    for (let filter of this.graphProps.filters) {
      let props: FilterProps = this.getFilterPropsForKind(filter.kind);

      let validators = [];

      if (props.isRequired)
        validators.push(Validators.required);

      if (props.min != null) {
        if (props.type == FilterType.NUMUPDOWN)
          validators.push(Validators.min(props.min));
        if (props.type == FilterType.TEXTBOX || props.type == FilterType.TEXTBOXADVANCED)
          validators.push(Validators.minLength(props.min));
      }

      if (props.max != null) {
        if (props.type == FilterType.NUMUPDOWN)
          validators.push(Validators.max(props.max));
        if (props.type == FilterType.TEXTBOX || props.type == FilterType.TEXTBOXADVANCED)
          validators.push(Validators.maxLength(props.max));
      }

      let actualValue: string | number | boolean | null;
      if (filter.latestValue == null || filter.latestValue === "")
        actualValue = null;
      else if (props.type == FilterType.NUMUPDOWN)
        actualValue = Number(filter.latestValue);
      else if (props.type == FilterType.CHECKBOX)
        actualValue = (filter.latestValue.length != 0 && filter.latestValue != "0" && filter.latestValue != "false");
      else
        actualValue = isNaN(Number(filter.latestValue)) ? filter.latestValue : Number(filter.latestValue);

      this.filtersForm.addControl(StatsGraphFilterKind[filter.kind], new UntypedFormControl(actualValue, validators));

      if (props.type == FilterType.TEXTBOXADVANCED) {
        this.filtersForm.addControl(StatsGraphFilterKind[filter.kind] + "-EXT", new UntypedFormControl(0));
      }
    }

    this.graphQuerySub = this.graphQuery.subscribe(() => {
      let tot: StatsGraphFilterInfo[] = [];

      for (let filter of this.graphProps.filters) {
        let value = this.filtersForm.controls[StatsGraphFilterKind[filter.kind]].value;

        let info = new StatsGraphFilterInfo();
        info.kind = filter.kind;
        info.latestValue = (value == null) ? null : String(value);
        tot.push(info);
      }

      let isNotAlways = (this.filtersForm.controls["period"].value == '1');
      let prefs = new StatsGraphPrefs();
      prefs.id = this.graphProps.graph.idGraph;
      prefs.prio = this.graphProps.graph.prio;
      prefs.isEnlarged = this.graphProps.graph.isEnlarged;
      prefs.latestShape = this.graphProps.graph.latestShape;
      prefs.latestDateStart = isNotAlways ? this.getNonTimezonedDate(this.filtersForm.controls["startDate"].value) : null;
      prefs.latestDateEnd = isNotAlways ? this.getNonTimezonedDate(this.filtersForm.controls["endDate"].value) : null;
      prefs.filters = tot;

      this.sendValues.emit(prefs);
    });

    this.refreshHeader();

    this.onLangChangeSub = this.translate.onLangChange.subscribe(() => {
      this.refreshHeader();
    });
  }

  ngAfterViewInit() {
  }

  ngAfterViewChecked() {
    if ((this.graphProps.graph.dataX == StatsGraphDataX.DATE) && this.canDateSub) {
      this.canDateSub = false;

      this.valueChangesSDSub = this.filtersForm.controls["startDate"].valueChanges.subscribe((value) => {
        this.refreshHeader();
        this.editValues.emit();
      });

      this.valueChangesEDSub = this.filtersForm.controls["endDate"].valueChanges.subscribe((value) => {
        this.refreshHeader();
        this.editValues.emit();
      });

      this.valueChangesPRSub = this.filtersForm.controls["period"].valueChanges.subscribe((value) => {
        this.refreshHeader();
        this.editValues.emit();
      });

      this.refreshHeader();
    }
  }

  ngOnDestroy() {
    if (this.onLangChangeSub != null) this.onLangChangeSub.unsubscribe();
    if (this.statsGetEarliestDateForDataSub != null) this.statsGetEarliestDateForDataSub.unsubscribe();
    if (this.graphQuerySub != null) this.graphQuerySub.unsubscribe();
    if (this.valueChangesSDSub != null) this.valueChangesSDSub.unsubscribe();
    if (this.valueChangesEDSub != null) this.valueChangesEDSub.unsubscribe();
    if (this.valueChangesPRSub != null) this.valueChangesPRSub.unsubscribe();
  }

  getFilterPropsForKind(kind: StatsGraphFilterKind): FilterProps {
    return filtersProps.find(filterProp => filterProp.kind == kind);
  }

  getNonTimezonedDate(controlValue: any, hasTime: boolean = true) {
    let d = new Date(controlValue);
    d = new Date(d.getFullYear(), d.getMonth(), d.getDate(), d.getHours(), d.getMinutes() - d.getTimezoneOffset(), d.getSeconds(), d.getMilliseconds());

    if (!hasTime)
      d = new Date(d.getFullYear(), d.getMonth(), d.getDate());

    return d;
  }

  graphSubmit() {
    if (!this.filtersForm.valid) {
      for (let name in this.filtersForm.controls) {
        let control = this.filtersForm.controls[name];
        if (control.invalid) {
          control.markAsTouched();
        }
      }
      return;
    }

    let dataX: { [key: string]: any } = null;
    let dataY: { [key: string]: any } = {};

    let dataXFilters: string[] = [
      "period", "startDate", "endDate"
    ];

    if (this.graphProps.graph.dataX == StatsGraphDataX.DATE) {
      dataX = { always: (this.filtersForm.controls["period"].value == '1') ? false : true };
      if (!dataX.always) {
        dataX.start = this.getNonTimezonedDate(this.filtersForm.controls["startDate"].value)
        dataX.end = this.getNonTimezonedDate(this.filtersForm.controls["endDate"].value)
      }
    }

    for (let control in this.filtersForm.controls) {
      if (dataXFilters.includes(control))
        continue;

      let val = this.filtersForm.controls[control].value;
      let type = (val == null) ? null : typeof val;

      dataY[control] = val;
    }

    let data = { dataX: dataX, dataY: dataY };

    this.submit.emit(data);
  }

  refreshHeader() {
    if (this.graphProps.graph.dataX == StatsGraphDataX.DATE) {
      let toSend = this.translate.instant('stats.common.period') + this.translate.instant('stats.common.colon');

      if (this.filtersForm.controls["period"].value == '1') {
        let language = this.storage.get("actualLanguage");
        if (language == "" || language == null)
          language = "fr";

        toSend += this.translate.instant('stats.graphs.datepicker.from') + " " + this.getNonTimezonedDate(this.filtersForm.controls["startDate"].value).toLocaleDateString(language) + " " + this.translate.instant('stats.graphs.datepicker.to') + " " + this.getNonTimezonedDate(this.filtersForm.controls["endDate"].value).toLocaleDateString(language);
      }
      else
        toSend += this.translate.instant('stats.graphs.datepicker.always');

      this.header.emit(toSend);
    }
  }
}
